import { LockOutlined } from "@ant-design/icons"
import { Tooltip } from "antd"
import dayjs from "dayjs"
import { useMemo } from "react"
import styled from "styled-components"

import StudentsIcon from "#assets/studentsIcon.svg?react"
import { classSessionAtoms } from "#atoms"
import AttendanceIcon from "#components/AttendanceIcon"
import { ATTENDANCE_STATUSES } from "#constants/globalConstants"
import {
  type ThemeContextType,
  useThemeContext,
} from "#contexts/ThemeContextProvider"
import { type ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { type AttendanceStatus } from "#customTypes/types"
import { useOneDoc } from "#hooks/jotai/useSubscription"
import { getSessionHeadcount } from "#utils/classHelpers"

import AnimateInFlex, { ANIMATION_BY_KEY } from "./AnimateInFlex"
import Flex from "./Flex"

type SessionBorderState = {
  isSelected: boolean
  isNewlySelected?: boolean
  isDropped?: boolean
  isDisabled: boolean
  isPast: boolean
  theme: ThemeContextType
}

const getSessionBorderColor = (
  state: SessionBorderState,
  isHover = false
): string => {
  const { isSelected, isNewlySelected, isDropped, isDisabled, isPast, theme } =
    state

  // Dropped sessions always use deselectedBorderColor
  if (isDropped) {
    return theme.sessionSelection.deselectedBorderColor
  }

  // Disabled sessions use borderColor with opacity
  if (isDisabled) {
    return `${theme.sessionSelection.borderColor}88`
  }

  // Selected sessions
  if (isSelected) {
    if (isNewlySelected) {
      // Newly selected: green on default, lighter border on hover
      return isHover
        ? theme.sessionSelection.borderColor
        : theme.sessionSelection.selectedBorderColor
    }
    // Previously selected: gray
    return theme.gray[5]
  }

  // Unselected sessions: past sessions use borderColor with opacity on hover, otherwise default
  if (isPast && isHover) {
    return `${theme.sessionSelection.borderColor}88`
  }

  return theme.sessionSelection.borderColor
}

const SSession = styled(Flex)<{
  isSelected: boolean
  isNewlySelected?: boolean
  isDropped?: boolean
  isDisabled: boolean
  isPast: boolean
  theme: ThemeContextType
}>`
  color: ${({ isPast, theme }) => (isPast ? theme.gray[6] : theme.black)};
  width: 106px;
  height: 74px;
  cursor: ${({ isDisabled }) => (isDisabled ? "default" : "pointer")};
  align-items: flex-start;
  position: relative;
  box-shadow: ${({ isSelected, theme }) =>
    isSelected ? `0 4px 4px ${theme.black}cc` : "none"};
  flex-direction: column;
  background: ${({ isPast, theme }) => (isPast ? theme.gray[1] : "white")};
  box-sizing: border-box;
  border-radius: 6px;
  position: relative;
  transition: all 0.2s ease-in-out;

  opacity: ${({ isPast, isSelected, isDisabled }) => {
    if (isDisabled) {
      return isPast ? 0.8 : 0.6
    }
    if (isPast) {
      return isSelected ? 0.8 : 0.6
    }
    return 1
  }};

  border: ${(state: SessionBorderState) => {
    const color = getSessionBorderColor(state, false)
    return `2px solid ${color}`
  }};

  &:hover {
    border: ${(state: SessionBorderState) => {
      // Disabled sessions are not hoverable
      if (state.isDisabled) {
        return
      }
      const color = getSessionBorderColor(state, true)
      return `2px solid ${color}`
    }};
    opacity: ${({ isPast, isSelected, isDisabled }) => {
      // disabled sessions are not hoverable
      if (isDisabled) {
        return
      }
      if (isPast) {
        return isSelected ? 0.9 : 0.8
      }
      return 0.9
    }};
  }
`

const SessionBox = ({
  sessionId,
  status,
  isSelected,
  isNewlySelected,
  isDropped,
  onClick,
  isDisabled,
  isPast,
  disabledReason,
}: {
  sessionId: string | null | undefined
  status: AttendanceStatus
  isSelected: boolean
  isNewlySelected?: boolean
  isDropped?: boolean
  onClick: () => void
  isDisabled: boolean
  isPast?: boolean
  disabledReason?: string
}) => {
  const theme = useThemeContext()

  const session = useOneDoc<ClassSessionConstructor>(
    classSessionAtoms,
    DOC_TYPES.CLASS_SESSIONS,
    sessionId
  )
  const date = session ? dayjs(session.eventStart).format("M/D") : ""
  const headcount = useMemo(() => getSessionHeadcount(session), [session])

  const handleClick = () => {
    // Block clicking if disabled (regardless of past status)
    if (isDisabled || !session) return
    onClick()
  }

  if (!session) {
    return null
  }

  return (
    <SSession
      onClick={handleClick}
      isSelected={isSelected}
      isNewlySelected={isNewlySelected}
      isDropped={isDropped}
      theme={theme}
      isDisabled={isDisabled}
      isPast={isPast || false}
    >
      <Flex
        style={{
          width: "100%",
          borderBottom: `1px solid ${theme.sessionSelection.borderColor}`,
          justifyContent: "space-between",
          padding: "4px 4px 4px 6px",
          borderRadius: "4px 4px 0 0",
          // If disabled, make header background color slightly transparent
          backgroundColor: `${theme.sessionSelection.headerBackgroundColor}${isDisabled ? "44" : "FF"}`,
          color: theme.white,
        }}
      >
        <AnimateInFlex
          animation={ANIMATION_BY_KEY.leftSm}
          style={{ justifyContent: "flex-start" }}
          grow
        >
          <h3 style={{ width: 45, textAlign: "center", paddingRight: 6 }}>
            {date}
          </h3>
        </AnimateInFlex>

        <Flex
          style={{
            justifyContent: "flex-end",
            borderLeft: `1px solid ${theme.sessionSelection.borderColor}`,
            paddingLeft: 8,
            paddingRight: 2,
          }}
        >
          <Flex style={{ alignItems: "center" }} gap={4}>
            <StudentsIcon />
            <span style={{ fontSize: 12 }}>{headcount}</span>
          </Flex>
        </Flex>
      </Flex>
      <Flex center style={{ width: "100%" }} grow>
        {/* Hide enrolled icon for now */}
        {status !== ATTENDANCE_STATUSES.ENROLLED && (
          <AttendanceIcon status={status} />
        )}
      </Flex>
      {isDisabled && (
        <Flex
          style={{
            cursor: disabledReason ? "pointer" : "default",
            position: "absolute",
            bottom: 2,
            right: 2,
          }}
        >
          <Tooltip title={disabledReason}>
            <LockOutlined style={{ color: theme.black }} />
          </Tooltip>
        </Flex>
      )}
    </SSession>
  )
}

export default SessionBox
